<template>
	<header :class="{ 'blank': isBlank, 'mobile': isMobile, 'is-open': isMobileMenuOpen }" @click.stop="" role="heading" aria-level="1">
		<MobileMenu v-if="isMobile" v-model="isMobileMenuOpen" :class="{ 'light': isMobileMenuOpen }" />
		<Teleport to="#teleports" :disabled="!isMobile">
			<nav :class="{ 'mobile': isMobile, 'is-open': isMobileMenuOpen }" @click.stop="" role="navigation">
				<div class="logo">
					<NuxtLink to="/" @click.native="closeMenu" aria-label="Zur Startseite">
						<img src="~/assets/media/i/logo-header.webp" alt="Mailuft Logo" width="41" height="40" />
					</NuxtLink>
				</div>
				<ul v-if="links" ref="links" role="menubar">
					<li v-if="isMobile" role="menuitem">
						<NuxtLink to="/" @click.native="closeMenu" aria-label="Zur Startseite">Startseite</NuxtLink>
					</li>
					<li v-for="(link, key) in links" :class="{ 'active': $route.path === link.to }" role="menuitem">
						<NuxtLink :to="link.to" @click.native="closeMenu" :key="key">{{ link.name }}</NuxtLink>
					</li>
				</ul>
			</nav>
		</Teleport>
	</header>
</template>

<script setup lang="ts">
import { useEventListener } from '@vueuse/core'

defineProps<{links: Array<{to: string, name: string}>}>()

const isBlank = ref(false)
const isMobile = ref(false)
const isMobileMenuOpen = ref(false)
const linksRef = useTemplateRef('links')
const linksWidth = ref(0)
const route = useRoute()

const calculateHeight = () =>
{
	if( route.meta.layout === 'page' )
	{
		return document.documentElement.scrollTop > 60
	}

	return document.documentElement.scrollTop > window.innerHeight * .8
}
const calculateMobile = () => linksWidth.value > window.innerWidth

const scrollEvt = () => window.requestAnimationFrame(() => isBlank.value = calculateHeight() )
const resizeEvt = () => window.requestAnimationFrame(() => isMobile.value = calculateMobile() )
const closeMenu = () => isMobileMenuOpen.value = false

useEventListener(document, 'click', closeMenu)
useEventListener(document, 'scroll', scrollEvt)
useEventListener(window, 'resize', resizeEvt)

onMounted(() =>
{
	linksWidth.value = (linksRef.value?.offsetWidth ?? 0) + 105

	scrollEvt()
	resizeEvt()
})
</script>

<style lang="scss" scoped>
header ul {
	li {
		@for $i from 1 through 10 {
			&:nth-child(#{$i}) {
				transition-delay: #{.9s + ($i - 1) * 0.15s};
			}
		}
	}
}
</style>

<style lang="css" scoped>
header
{
	--blank-duration: .3s;
	display: flex;
	justify-content: center;
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	padding: 1rem;
	box-sizing: border-box;
	background: transparent;
	transition: opacity 1.65s ease-in, transform 1.5s ease-out, background-color var(--blank-duration) ease;
	will-change: background-color, opacity, transform, box-shadow;
	z-index: 1000;
}

header.blank:not(.mobile)
{
	background-color: rgb(var(--white));
	box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 2px 0px;
}

header.mobile
{
	justify-content: flex-end;
	box-shadow: none;
}

header.mobile.is-open
{
	backdrop-filter: none;
}

nav
{
	display: flex;
	justify-content: center;
	align-items: center;
	flex: 1;
	gap: 1rem;
}

nav div.logo
{
	line-height: 0;
	margin: 0;
}

nav div.logo img
{
	display: block;
	width: auto;
	height: 40px;
	flex: 0;
}

nav > ul
{
	display: flex;
	justify-content: center;
	margin: 0;
	padding: 0;
	list-style: none;
}

nav > ul > li
{
	display: block;
	flex: 0;
}

nav > ul > li a
{
	display: block;
	font-family: "brother-1816", sans-serif;
	white-space: nowrap;
	color: rgb(var(--white));
	padding: .75rem 1.5rem;
	text-decoration: none;
	text-transform: uppercase;
	letter-spacing: 3px;
	font-size: .95em;
	font-weight: 500;
	transition: opacity .35s ease, text-shadow .35s ease, color var(--blank-duration) ease;
	text-shadow: none;	
	opacity: .7;
}

nav > ul > li a::after
{
	content: '';
	position: absolute;
	bottom: 0;
	left: 1rem;
	height: 3px;
	background-color: rgb(var(--blue));
	width: 0;
	opacity: 0;
	transition: width .4s ease, opacity var(--blank-duration) ease;
}

header.blank:not(.mobile) nav > ul > li a
{
	position: relative;
	opacity: 1;
	color: rgb(var(--black));
}

header.blank:not(.mobile) nav > ul > li a::after
{
	opacity: 1;
}

nav > ul > li.active a,
nav > ul > li a:hover
{
	text-shadow: 0 0 5px #fff;
	opacity: 1;
}

header.blank:not(.mobile) nav > ul > li.active a,
header.blank:not(.mobile) nav > ul > li a:hover
{
	text-shadow: none;
	color: rgb(var(--blue));
}

header.blank:not(.mobile) nav > ul > li.active a::after,
header.blank:not(.mobile) nav > ul > li a:hover::after
{
	width: calc(100% - 2rem);
}

nav.mobile
{
	position: fixed;
	flex: none;
	top: 0;
	left: 0;
	width: 100%;
	height: 100vh;
	align-items: flex-start;
	flex-direction: column;
	background-color: rgba(var(--black),.8);
	transition: transform .5s ease;
	transform: translateX(-100%);
	backdrop-filter: saturate(180%) blur(20px);
	z-index: 990;
}

nav.mobile.is-open
{
	transform: translateX(0);
}

nav.mobile > div.logo
{
	padding: 0 2.5rem;
}

nav.mobile > ul
{
	align-items: flex-start;
	flex-direction: column;
}
nav.mobile > ul li a
{
	padding: 1rem 2.25rem;
	font-size: 1.2rem;
}
</style>